export default function GuideContent() {
  const sections = [
    {
      id: 'intro',
      title: '高級腕時計の世界へようこそ',
      content: '本ガイドでは、はじめての方でも安心して「腕時計の基礎知識」を学び、自信をもって購入に臨めるよう、必要な情報を網羅的にお届けします。以下の流れで、機械式時計の仕組みや素材選び、デザイン、購入時の注意点まで順を追って解説します。'
    },
    {
      id: 'what-is',
      title: '1. 高級腕時計とは',
      content: '高級腕時計は単なる時刻表示の道具ではなく、熟練した技術者が手作業で組み上げる精巧なムーブメントと、上質な素材、ブランドが築いてきた物語性を併せ持つ"タイムピース"です。歯車やゼンマイが生み出す機械式の動きは、毎日身につけるほどに味わいが増し、オーバーホールを重ねながら一生ものの相棒になってくれます。また、希少素材や限定生産モデルには「時間を超えた投資価値」があり、資産としての側面も備えています。着用シーンや所有感、長期保有を前提としたサポート体制まで含めて評価することで、本当に自分に合った一生ものを見つけられます。',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20mechanical%20watch%20movement%20with%20intricate%20gears%20and%20springs%2C%20close-up%20macro%20photography%20showing%20craftsmanship%20details%2C%20golden%20components%2C%20Swiss%20watchmaking%20precision%2C%20elegant%20timepiece%20mechanism%20on%20white%20marble%20surface%20with%20soft%20studio%20lighting&width=800&height=500&seq=guide-what-is&orientation=landscape'
    },
    {
      id: 'movement',
      title: '2. ムーブメントの基礎知識',
      subsections: [
        {
          subtitle: '自動巻き(オートマチック)',
          content: '腕の振りでローターが回転し、ゼンマイを自動で巻き上げる仕組み。日常的に身につけることでパワーリザーブ(動力持続時間)をキープでき、定期的な巻き上げが不要です。精緻な機構ゆえに数年ごとのオーバーホールが必要ですが、その分メンテナンスを通じて長く愛用できます。'
        },
        {
          subtitle: '手巻き(マニュアルワインド)',
          content: 'リューズを手動で巻き上げるシンプルな駆動方式。機構がコンパクトなので薄型モデルが多く、巻くたびに"自分の手"で時を刻んでいる実感が楽しめます。巻き忘れを防ぐため、毎朝のルーティンに組み込むと良いでしょう。'
        },
        {
          subtitle: 'クォーツ',
          content: '水晶振動子で高精度を実現した電池駆動方式。機械式に比べて手頃な価格帯が多く、日常使いの利便性抜群。年差±10秒前後の高精度モデルやソーラー充電式など、バリエーションも豊富です。メンテナンスは主に電池交換のみで手軽ですが、"機械式の味わい"は得にくい点を考慮しましょう。'
        }
      ],
      image: 'https://readdy.ai/api/search-image?query=Three%20different%20watch%20movements%20side%20by%20side%20comparison%2C%20automatic%20rotor%20mechanism%2C%20manual%20winding%20crown%2C%20quartz%20crystal%20oscillator%2C%20technical%20illustration%20style%20with%20clean%20white%20background%2C%20detailed%20mechanical%20components%20visible&width=800&height=500&seq=guide-movement&orientation=landscape'
    },
    {
      id: 'materials',
      title: '3. ケース素材とサイズ選び',
      subsections: [
        {
          subtitle: 'ステンレススチール',
          content: '耐久性とコストパフォーマンスに優れ、ビジネス・カジュアルどちらにもマッチ。定番素材として最も汎用性が高いです。'
        },
        {
          subtitle: 'ゴールド(イエロー/ホワイト/ローズ)',
          content: '高級感と資産性を兼ね備えた王道素材。柔らかな色合いはドレスウォッチに多く、日常使いにはやや贅沢ですが、特別な1本としておすすめです。'
        },
        {
          subtitle: 'チタン',
          content: 'ステンレスより軽量かつ肌に優しいチタン合金を使用。スポーツモデルやミリタリーテイストに多く、長時間着用でも疲れにくいのが魅力です。'
        },
        {
          subtitle: 'セラミック',
          content: '傷つきにくく発色が美しいハイテクセラミックをケース・ベゼルに採用。スタイリッシュな外観と高い耐久性を両立します。'
        }
      ],
      additionalContent: 'ケース径は男性は36〜42mm、女性は28〜34mm程度が一般的ですが、手首の幅や装着感、着用シーンに合わせて選びましょう。実際に試着し、バランスや重さを確かめるのがベストです。',
      image: 'https://readdy.ai/api/search-image?query=Four%20luxury%20watch%20cases%20in%20different%20materials%2C%20stainless%20steel%20polished%20finish%2C%20yellow%20gold%20elegant%20shine%2C%20titanium%20matte%20texture%2C%20black%20ceramic%20glossy%20surface%2C%20arranged%20on%20white%20silk%20fabric%20with%20soft%20natural%20lighting&width=800&height=500&seq=guide-materials&orientation=landscape'
    },
    {
      id: 'design',
      title: '4. デザインとブランドのポイント',
      content: 'クラシックからスポーツ、ドレス、ミリタリーまで多彩なジャンルがある高級腕時計。',
      subsections: [
        {
          subtitle: 'ロレックス',
          content: '堅牢性に優れたケース設計と高い資産価値を持つため、初心者からコレクターまで幅広く支持されます。サブマリーナーやデイトナなどアイコンモデルは中古市場でも安定した人気。'
        },
        {
          subtitle: 'オメガ',
          content: 'ムーンウォッチとして知られるスピードマスターや、耐磁性能を備えたシーマスターなど、技術革新と歴史的裏付けが魅力。比較的手に届きやすい価格帯からラグジュアリーラインまで揃います。'
        },
        {
          subtitle: 'カルティエ',
          content: 'ジュエラー発祥らしいエレガントなデザインが強み。タンクやサントスなどの定番モデルは、時計としてだけでなくファッションアイテムとしても高い評価を得ています。'
        },
        {
          subtitle: 'パテック・フィリップ、オーデマ・ピゲなど',
          content: '超高級ラインは希少性も高く、資産性を重視するなら要チェック。'
        }
      ],
      additionalContent: 'ブランドごとの歴史やアイコンモデル、ケース形状や文字盤デザインの特徴を比較し、自分のライフスタイルや好みに合う一本を選びましょう。',
      image: 'https://readdy.ai/api/search-image?query=Collection%20of%20iconic%20luxury%20watches%20from%20different%20prestigious%20brands%2C%20classic%20dress%20watches%2C%20sports%20chronographs%2C%20elegant%20timepieces%20displayed%20on%20dark%20velvet%20surface%2C%20professional%20product%20photography%20with%20dramatic%20lighting&width=800&height=500&seq=guide-design&orientation=landscape'
    },
    {
      id: 'purchase',
      title: '5. 購入時の注意点・メンテナンス',
      subsections: [
        {
          subtitle: '購入ルートの選定',
          content: '正規代理店、メーカー直営ブティック、認定中古販売店など、信頼できるチャネルで購入することが最優先。並行輸入品も魅力的ですが、保証やアフターサービスの内容をよく確認してください。'
        },
        {
          subtitle: '保証書・シリアル番号',
          content: '購入時に保証書が正規品である証明となります。シリアル番号がケースと一致しているか、改ざんの跡がないかを必ずチェックしましょう。'
        },
        {
          subtitle: 'オーバーホール',
          content: '機械式時計は定期的な分解清掃(オーバーホール)が必要です。メーカー推奨の期間(通常3〜5年)を把握し、費用・期間を事前に見積もっておくと安心です。'
        },
        {
          subtitle: '偽物対策',
          content: '写真やネット通販で購入する場合は、文字盤の細部、ロゴのフォント、針・インデックスの仕上げなどを比較。少しでも違和感があれば専門家に相談してください。'
        },
        {
          subtitle: '保管・取り扱い',
          content: '防水性能を過信せず、水仕事や高温多湿を避ける。長期間外す場合はムーブメントに負荷をかけない程度に動かすか、ワインディングマシンを利用するとムーブメント劣化を防げます。'
        }
      ],
      additionalContent: 'これらのポイントを押さえれば、安心して自分だけの"一生もの"高級腕時計を手に入れ、長く楽しむことができます。',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20watch%20maintenance%20and%20care%20scene%2C%20watchmaker%20examining%20timepiece%20with%20loupe%2C%20warranty%20certificate%20and%20authentication%20documents%2C%20professional%20watch%20service%20tools%20on%20clean%20white%20workspace%20with%20soft%20lighting&width=800&height=500&seq=guide-purchase&orientation=landscape'
    }
  ];

  return (
    <main className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-amber-50 via-white to-amber-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-full mb-6">
            完全ガイド
          </div>
          <h1 className="text-4xl md:text-6xl font-light text-gray-900 mb-6 tracking-wide">
            高級腕時計<br className="md:hidden" />購入ガイド
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            はじめての方でも安心して学べる、腕時計の基礎知識
          </p>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {sections.map((section, index) => (
            <article key={section.id} id={section.id} className={`${index !== 0 ? 'mt-20' : ''}`}>
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                  {section.title}
                </h2>
                {section.content && (
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                    {section.content}
                  </p>
                )}
              </div>

              {section.image && (
                <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
                  <div className="relative w-full h-96">
                    <img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              )}

              {section.subsections && (
                <div className="space-y-6 mb-6">
                  {section.subsections.map((subsection, subIndex) => (
                    <div key={subIndex} className="bg-amber-50/50 rounded-lg p-6 border border-amber-100">
                      <h3 className="text-xl md:text-2xl font-medium text-gray-900 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-amber-600 rounded-full mr-3"></span>
                        {subsection.subtitle}
                      </h3>
                      <p className="text-base text-gray-700 leading-relaxed pl-5">
                        {subsection.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {section.additionalContent && (
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  {section.additionalContent}
                </p>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-600 to-amber-700">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            あなたの一生ものを見つけませんか?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            ChronoCharmでは、厳選された高級腕時計の情報をお届けしています
          </p>
          <button
            onClick={() => {
              if (window.REACT_APP_NAVIGATE) {
                window.REACT_APP_NAVIGATE('/');
              }
            }}
            className="px-10 py-4 bg-white text-amber-600 text-base font-medium rounded-md hover:bg-amber-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            ホームに戻る
          </button>
        </div>
      </section>
    </main>
  );
}
